/**
 * Which KIND of thing this is: project, deliverable or task.
 *
 * Three text tags — `PRJ`, `DLV`, `TSK` — replacing the square, diamond and
 * circle that did this job until 4 Oct 2026.
 *
 * **The rule they replace was about colour blindness**, and it survives the
 * change rather than being dropped: *"each gets a colour AND a shape, not
 * colour alone — roughly one man in twelve cannot separate these hues, and a
 * square is still a square."* Three letters are a stronger answer than three
 * shapes, because they need no legend and survive at any size. That is why the
 * swap was accepted; it is not a cosmetic preference.
 *
 * The tag REPLACES the words that used to sit beside it. `<span className="kind
 * deliverable">Deliverable</span>` became `<KindTag kind="deliverable" />` —
 * the label and the mark were always saying the same thing twice.
 */
export type Kind = "project" | "deliverable" | "task";

/**
 * Where it is being used, not how big it should be.
 *
 * Naming these `sm`/`md`/`lg` would invite picking one by eye; naming them for
 * the three places tags appear means the size is a consequence of the
 * position, which is what keeps them consistent across nine screens.
 */
export type KindTagSize = "crumb" | "row" | "head";

const TEXT: Record<Kind, string> = {
  project: "PRJ",
  deliverable: "DLV",
  task: "TSK",
};

export function KindTag({
  kind,
  size = "row",
  className = "",
}: {
  kind: Kind;
  size?: KindTagSize;
  className?: string;
}) {
  return (
    <span
      className={`kind-tag kt-${kind} kt-${size} ${className}`.trim()}
      /*
       * The letters are an abbreviation, so a screen reader should say the
       * word. `aria-label` on a span with no role is ignored by some readers,
       * which is why the full word is real text, visually hidden, and the
       * letters themselves are hidden from the accessibility tree instead.
       */
      role="img"
      aria-label={kind}
    >
      <span aria-hidden="true">{TEXT[kind]}</span>
    </span>
  );
}
